"use client";
import { ChoicePills, WhatsAppPhone } from "@/components/marketing";
import type {
  ChoiceOption,
  WhatsAppChatHeader,
} from "@/components/marketing";
import { useState } from "react";
import { fillTemplate } from "../../guidePage.util";
import DemoPanel, {
  demoGridClass,
  demoHeadingClass,
} from "../demoPanel/demoPanel.component";

interface CampaignTiming extends ChoiceOption {
  // What the form shows in the Send row, and the note under the button.
  value: string;
  note: string;
}

export interface CampaignBuilderCopy {
  targetHeading: string;
  targets: ChoiceOption[];
  offerHeading: string;
  offers: ChoiceOption[];
  timingHeading: string;
  timings: CampaignTiming[];
  fine: string;
  formTitle: string;
  rowLabels: { target: string; offer: string; channel: string; send: string };
  // `{group}` is the target's label.
  targetTemplate: string;
  channel: string;
  sendButton: string;
  // messages[target][offer].
  messages: Record<string, Record<string, string>>;
  chat: WhatsAppChatHeader;
  time: string;
  initial: { target: string; offer: string; timing: string };
}

interface CampaignBuilderProps {
  copy: CampaignBuilderCopy;
}

const labelOf = (options: ChoiceOption[], key: string) =>
  options.find((option) => option.key === key)?.label ?? key;

// The preview's `campaign_builder_guide`: choose a group, an offer and a
// time, and see the Create Campaign form and the message a customer gets.
const CampaignBuilder = ({ copy }: CampaignBuilderProps) => {
  const initialState = { ...copy.initial };
  const [state, setState] = useState(initialState);
  const update = (patch: Partial<typeof initialState>) =>
    setState((state) => ({ ...state, ...patch }));

  const timing =
    copy.timings.find((item) => item.key === state.timing) ?? copy.timings[0];
  const rows = [
    {
      label: copy.rowLabels.target,
      value: fillTemplate(copy.targetTemplate, {
        group: labelOf(copy.targets, state.target),
      }),
    },
    { label: copy.rowLabels.offer, value: labelOf(copy.offers, state.offer) },
    { label: copy.rowLabels.channel, value: copy.channel },
    { label: copy.rowLabels.send, value: timing.value },
  ];

  return (
    <div className={demoGridClass}>
      <DemoPanel>
        <h3 className={demoHeadingClass}>{copy.targetHeading}</h3>
        <ChoicePills
          ariaLabel={copy.targetHeading}
          options={copy.targets}
          value={state.target}
          onChange={(target) => update({ target })}
        />
        <h3 className={demoHeadingClass}>{copy.offerHeading}</h3>
        <ChoicePills
          ariaLabel={copy.offerHeading}
          options={copy.offers}
          value={state.offer}
          onChange={(offer) => update({ offer })}
        />
        <h3 className={demoHeadingClass}>{copy.timingHeading}</h3>
        <ChoicePills
          ariaLabel={copy.timingHeading}
          options={copy.timings}
          value={state.timing}
          onChange={(timing) => update({ timing })}
        />
        <p className="text-sm leading-normal text-ink-muted">{copy.fine}</p>
      </DemoPanel>
      <DemoPanel className="justify-items-center">
        <div
          aria-live="polite"
          className="grid w-full gap-2.5 rounded-[22px] border-2 border-line bg-white p-[18px]"
        >
          <b className="text-lg">{copy.formTitle}</b>
          <dl className="grid grid-cols-[90px_minmax(0,1fr)] gap-x-3 gap-y-2">
            {rows.map((row) => (
              <div key={row.label} className="contents">
                <dt className="text-[13.5px] text-ink-muted">{row.label}</dt>
                <dd className="text-[15px] font-bold">{row.value}</dd>
              </div>
            ))}
          </dl>
          <span
            aria-hidden="true"
            className="rounded-full bg-primary p-3 text-center font-extrabold text-white"
          >
            {copy.sendButton}
          </span>
          <small className="text-[13px] text-ink-muted">{timing.note}</small>
        </div>
        <WhatsAppPhone
          chat={copy.chat}
          messages={[
            { text: copy.messages[state.target][state.offer], time: copy.time },
          ]}
        />
      </DemoPanel>
    </div>
  );
};

export default CampaignBuilder;
